import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import { useBankStatements } from 'hooks/api/useBanking';
import { coaLedgerService, bankStatementService } from 'api/services';
import notificationService from 'services/notificationService';
import type { BankStatementDto, BankStatementQueryParams } from 'api/types';
import { ImportStatementWizard } from '../../modals/modules/banking/ImportStatementWizard';
import {
  ReconciliationWorksheet, statementStatusBadge, statementStatusLabel,
  type WorksheetLineFilters, worksheetDefaultFilters, MATCH_STATUS_OPTIONS, DIRECTION_OPTIONS,
} from '../../modals/modules/banking/ReconciliationWorksheet';
import { ReconciliationSuggestionsModal } from '../../modals/modules/banking/ReconciliationSuggestionsModal';
import { ChargeRulesModal } from '../../modals/modules/banking/ChargeRulesModal';

// ============================================================================
// BANK STATEMENT SCREEN (ADR-0024 slice ④) — statement register + import
// wizard + per-statement reconciliation worksheet.
// ============================================================================

type ViewMode = 'list' | 'grid';

interface BankStatementScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATEMENT_STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'UPLOADED', label: 'Uploaded' },
  { value: 'PARTIALLY_MATCHED', label: 'Partially Matched' },
  { value: 'COMPLETED', label: 'Completed' },
];

const BankStatementScreen: React.FC<BankStatementScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('bank-statement');
  const { ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [queryParams, setQueryParams] = useState<BankStatementQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useBankStatements(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [bankFilter, setBankFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const bankLabelCache = useRef<Map<string, string>>(new Map());

  const [wizardOpen, setWizardOpen] = useState(false);
  // In-screen navigation: the worksheet renders WITHIN this screen (not a modal —
  // ActionMenu portals at zIndex 9999 would sit behind --z-modal). The register's
  // grid/filter state stays alive in this component while the worksheet shows.
  const [view, setView] = useState<'register' | 'worksheet'>('register');
  const [selectedStatement, setSelectedStatement] = useState<BankStatementDto | null>(null);

  // --- Worksheet chrome state (separate from the register's — no leakage) ---
  const [wsFilterOpen, setWsFilterOpen] = useState(false);
  const [wsDraft, setWsDraft] = useState<WorksheetLineFilters>(worksheetDefaultFilters('reconcile'));
  const [wsApplied, setWsApplied] = useState<WorksheetLineFilters>(worksheetDefaultFilters('reconcile'));
  const [wsSearchInput, setWsSearchInput] = useState('');
  const [wsSearchTerm, setWsSearchTerm] = useState('');
  const [wsRefreshKey, setWsRefreshKey] = useState(0);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [chargeRulesOpen, setChargeRulesOpen] = useState(false);

  // Debounced worksheet search — ORs description+reference server-side, ANDed
  // with the filter panel's narrowing.
  useEffect(() => {
    const t = setTimeout(() => setWsSearchTerm(wsSearchInput), 400);
    return () => clearTimeout(t);
  }, [wsSearchInput]);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const openWorksheet = useCallback((statement: BankStatementDto, mode: 'reconcile' | 'view') => {
    setSelectedStatement(statement);
    const defaults = worksheetDefaultFilters(mode);
    setWsDraft(defaults);
    setWsApplied(defaults);
    setWsSearchInput(''); setWsSearchTerm('');
    setWsFilterOpen(false);
    setView('worksheet');
  }, []);

  const handleBackToRegister = useCallback(() => {
    setView('register');
    setSelectedStatement(null);
    setWsFilterOpen(false);
    refetch();
  }, [refetch]);

  // Suggestions are reviewed and confirmed in a dialog; nothing is matched without the user.
  const handleSuggestionsConfirmed = useCallback(() => {
    setWsRefreshKey(k => k + 1);
    refetch();
  }, [refetch]);

  const handleWsRefresh = useCallback(() => setWsRefreshKey(k => k + 1), []);

  const updWsDraft = useCallback((patch: Partial<WorksheetLineFilters>) => setWsDraft(prev => ({ ...prev, ...patch })), []);

  const applyWsFilters = useCallback(() => {
    setWsApplied(wsDraft);
    setWsFilterOpen(false);
  }, [wsDraft]);

  const clearWsFilters = useCallback(() => {
    const blank: WorksheetLineFilters = {
      matchStatus: '', direction: '', fromDate: '', toDate: '',
      minAmount: '', maxAmount: '', reference: '', description: '',
    };
    setWsDraft(blank);
    setWsApplied(blank);
  }, []);

  const handleImported = useCallback((statement: BankStatementDto) => {
    setWizardOpen(false);
    refetch();
    openWorksheet(statement, 'reconcile');
  }, [refetch, openWorksheet]);

  const tableColumns: SyutGridColumn<BankStatementDto>[] = useMemo(() => [
    {
      id: 'bank', header: 'Bank', accessorKey: 'bankLedgerName', size: 170,
      cell: ({ row }) => (row.original as BankStatementDto).bankLedgerName || '—',
    },
    {
      id: 'statementRef', header: 'Statement Ref', accessorKey: 'statementRef', size: 130,
      cell: ({ row }) => (row.original as BankStatementDto).statementRef || '—',
    },
    {
      id: 'period', header: 'Period', size: 175,
      cell: ({ row }) => {
        const s = row.original as BankStatementDto;
        return `${fmtDate(s.periodFrom)} – ${fmtDate(s.periodTo)}`;
      },
    },
    {
      id: 'file', header: 'File', accessorKey: 'fileName', size: 160,
      cell: ({ row }) => (row.original as BankStatementDto).fileName || '—',
    },
    {
      id: 'lines', header: 'Lines', accessorKey: 'lineCount', size: 70,
      cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{(row.original as BankStatementDto).lineCount}</span>,
    },
    {
      id: 'unmatched', header: 'Unmatched Deposits', accessorKey: 'unmatchedCount', size: 130,
      cell: ({ row }) => {
        const n = (row.original as BankStatementDto).unmatchedCount;
        return (
          <span style={{ display: 'block', textAlign: 'right', fontWeight: n > 0 ? 700 : 400, color: n > 0 ? '#d97706' : 'var(--text-muted)' }}>
            {n}
          </span>
        );
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statementStatus', size: 145,
      cell: ({ row }) => statementStatusBadge((row.original as BankStatementDto).statementStatus),
    },
    {
      id: 'imported', header: 'Imported On / By', size: 175,
      cell: ({ row }) => {
        const s = row.original as BankStatementDto;
        return `${s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}${s.createdByName ? ` · ${s.createdByName}` : ''}`;
      },
    },
  ], []);

  const getRowActions = useCallback((rec: BankStatementDto): ActionMenuItem[] => [
    { id: 'worksheet', label: 'Open Worksheet', icon: <IconSystem.check size={18} />, onClick: () => openWorksheet(rec, 'reconcile') },
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => openWorksheet(rec, 'view') },
  ], [openWorksheet]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.custom('import', <IconSystem.upload size={18} />, 'Import Statement', () => setWizardOpen(true)), visible: can('IMPORT') },
    actionButtonFactory.refresh(refetch),
  ];

  const loadBankOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined, ledgerNature: 'BANK' });
      res.data.forEach(l => bankLabelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return [
        { value: '', label: 'All Banks' },
        ...res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` })),
      ];
    } catch {
      return [{ value: '', label: 'All Banks' }];
    }
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      bankLedgerId: bankFilter || undefined,
      statementStatus: statusFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [bankFilter, statusFilter, fromDate, toDate]);

  const clearFilters = useCallback(() => {
    setBankFilter(''); setStatusFilter(''); setFromDate(''); setToDate('');
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const appliedCount = [bankFilter, statusFilter, fromDate, toDate].filter(Boolean).length;

  const inWorksheet = view === 'worksheet' && !!selectedStatement;
  // Two chrome lines only (title = identity, description = live numbers).
  // Title prefers `{bank} · {ref}`; the period always lives in the description.
  const worksheetCrumb = selectedStatement
    ? `${selectedStatement.bankLedgerName || 'Bank'} · ${selectedStatement.statementRef || `${fmtDate(selectedStatement.periodFrom)} – ${fmtDate(selectedStatement.periodTo)}`}`
    : '';
  // Live numbers — selectedStatement is refreshed via onDetailRefreshed, so
  // auto-match / refresh update these immediately. Only existing parts render.
  const worksheetDescription = selectedStatement
    ? [
        (selectedStatement.periodFrom || selectedStatement.periodTo)
          ? `${fmtDate(selectedStatement.periodFrom)} – ${fmtDate(selectedStatement.periodTo)}` : '',
        selectedStatement.openingBalance != null ? `Opening ${money(selectedStatement.openingBalance)}` : '',
        selectedStatement.closingBalance != null ? `Closing ${money(selectedStatement.closingBalance)}` : '',
        `${selectedStatement.lineCount} lines`,
        `${selectedStatement.unmatchedCount} unmatched`,
        statementStatusLabel(selectedStatement.statementStatus),
      ].filter(Boolean).join(' · ')
    : '';
  const worksheetActionButtons: ActionButton[] = [
    {
      ...actionButtonFactory.custom('suggest', <IconSystem.checkCircle size={18} />, 'Suggest Matches', () => setSuggestionsOpen(true),
        { tooltip: 'Find likely book entries for the unmatched lines — you confirm them' }),
      variant: 'primary',
      visible: can('UPDATE'),
    },
    actionButtonFactory.custom('charge-rules', <IconSystem.settings size={18} />, 'Charge Rules', () => setChargeRulesOpen(true),
      { tooltip: 'Expected bank / gateway charges per payment mode, and the rate actually paid' }),
    actionButtonFactory.refresh(handleWsRefresh, 'Refresh lines and statement totals'),
    actionButtonFactory.custom('back', <IconSystem.arrowLeft size={18} />, 'Back to Register', handleBackToRegister,
      { tooltip: 'Back to the statement register' }),
  ];
  const wsAppliedCount = [
    wsApplied.matchStatus, wsApplied.direction, wsApplied.fromDate, wsApplied.toDate,
    wsApplied.minAmount, wsApplied.maxAmount, wsApplied.reference, wsApplied.description,
  ].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title={inWorksheet ? `Bank Statements › ${worksheetCrumb}` : 'Bank Statements'}
      description={inWorksheet
        ? worksheetDescription
        : 'Import bank statements, then reconcile deposits and withdrawals against the bank book whenever convenient'}
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={inWorksheet ? worksheetActionButtons : actionButtons}
      searchConfig={inWorksheet
        ? { value: wsSearchInput, onChange: setWsSearchInput, placeholder: 'Search description / reference…' }
        : { value: searchQuery, onChange: setSearchQuery, placeholder: 'Search statement ref / file name...' }}
      viewModeConfig={inWorksheet ? undefined : { currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={inWorksheet
        ? { onClick: () => setWsFilterOpen(!wsFilterOpen), isActive: wsFilterOpen, appliedCount: wsAppliedCount }
        : { onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        inWorksheet ? (wsFilterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setWsFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Show" options={DIRECTION_OPTIONS} value={wsDraft.direction}
                  onChange={(v) => updWsDraft({ direction: (v as string) ?? '' })}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Match Status" options={MATCH_STATUS_OPTIONS} value={wsDraft.matchStatus}
                  onChange={(v) => updWsDraft({ matchStatus: (v as string) ?? '' })}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={wsDraft.fromDate}
                  onChange={(e) => updWsDraft({ fromDate: e.target.value })}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={wsDraft.toDate}
                  onChange={(e) => updWsDraft({ toDate: e.target.value })}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Amount From</label>
                <input type="number" inputMode="decimal" className="form-input" value={wsDraft.minAmount}
                  onChange={(e) => updWsDraft({ minAmount: e.target.value })}
                  placeholder="Min amount" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Amount To</label>
                <input type="number" inputMode="decimal" className="form-input" value={wsDraft.maxAmount}
                  onChange={(e) => updWsDraft({ maxAmount: e.target.value })}
                  placeholder="Max amount" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Reference</label>
                <input type="text" className="form-input" value={wsDraft.reference}
                  onChange={(e) => updWsDraft({ reference: e.target.value })}
                  placeholder="Contains..." style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Description</label>
                <input type="text" className="form-input" value={wsDraft.description}
                  onChange={(e) => updWsDraft({ description: e.target.value })}
                  placeholder="Contains..." style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearWsFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyWsFilters}>Apply</button>
            </div>
          </div>
        )) : (filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Bank" isAsync loadOptions={loadBankOptions}
                  defaultOptions cacheOptions={false} value={bankFilter}
                  seedOptions={bankFilter ? [{ value: bankFilter, label: bankLabelCache.current.get(bankFilter) || '' }] : undefined}
                  onChange={(v) => setBankFilter((v as string) || '')}
                  placeholder="All Banks" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Status" options={STATEMENT_STATUS_OPTIONS} value={statusFilter}
                  onChange={(v) => setStatusFilter((v as string) || '')}
                  placeholder="All Statuses" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        ))
      }
    >
      {/* Worksheet view — in-screen, same tab; register state stays alive below. */}
      {inWorksheet && selectedStatement && (
        <div className="data-grid-container">
          <ReconciliationWorksheet
            statement={selectedStatement}
            filters={wsApplied}
            searchTerm={wsSearchTerm}
            refreshKey={wsRefreshKey}
            canUpdate={can('UPDATE')} canCreate={can('CREATE')}
            onChanged={refetch}
            onDetailRefreshed={setSelectedStatement} />
        </div>
      )}

      {!inWorksheet && (
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No statements yet — click Import Statement to upload one.'}
            frozenRightColumns={['actions']} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.file size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No statements yet.'}</p>
                </div>
              ) : (
                data.map((s) => (
                  <div key={s.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{s.statementRef || s.fileName || '—'}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>
                          {s.bankLedgerName || 'Bank'}
                        </h3>
                      </div>
                      <ActionMenu items={getRowActions(s)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {fmtDate(s.periodFrom)} – {fmtDate(s.periodTo)} · {s.lineCount} lines
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {statementStatusBadge(s.statementStatus)}
                      {s.unmatchedCount > 0 && (
                        <span className="badge" style={{ background: '#d97706', color: '#fff' }}>
                          {s.unmatchedCount} unmatched
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      <ReconciliationSuggestionsModal isOpen={suggestionsOpen} statementId={selectedStatement?.id ?? null}
        canUpdate={can('UPDATE')}
        onConfirmed={handleSuggestionsConfirmed}
        onClose={() => setSuggestionsOpen(false)} />

      {selectedStatement && (
        <ChargeRulesModal isOpen={chargeRulesOpen} bankLedgerId={selectedStatement.bankLedgerId}
          bankLedgerName={selectedStatement.bankLedgerName} canUpdate={can('UPDATE')}
          onClose={() => setChargeRulesOpen(false)} />
      )}

      <ImportStatementWizard isOpen={wizardOpen}
        onImported={handleImported}
        onClose={() => setWizardOpen(false)} />
    </EnterpriseScreenShell>
  );
};

export default BankStatementScreen;
